import { MoneyAmount } from '../value-objects/MoneyAmount';
import { YearResult } from '../entities/YearResult';
import { MethodResult } from '../entities/MethodResult';
import { FireScenario } from '../entities/FireScenario';
import { FireMethod, EventKind } from '../enums';
import { BeginningOfYearPolicy, TimingPolicy } from './TimingPolicy';
import { getPensionDefaults } from '../data/pensionValidation';

export class SimulationEngine {
  private readonly policy: TimingPolicy;

  constructor(policy?: TimingPolicy) {
    this.policy = policy ?? new BeginningOfYearPolicy();
  }

  simulate(
    scenario: FireScenario,
    method: FireMethod,
    methodResult: MethodResult,
    horizonAge: number,
  ): YearResult[] {
    const t = scenario.yearsToTarget;
    const totalYears = horizonAge - scenario.currentAge.value;
    const results: YearResult[] = [];

    for (let y = 0; y < totalYears; y++) {
      const portfolioStart =
        results.length > 0
          ? results[results.length - 1]!.portfolioEnd
          : scenario.currentPortfolio;
      results.push(
        this.simulateSingleYear(scenario, method, methodResult, y, t, portfolioStart),
      );
    }

    return results;
  }

  simulateSingleYear(
    scenario: FireScenario,
    _method: FireMethod,
    _methodResult: MethodResult,
    yearIndex: number,
    t: number,
    portfolioStart: MoneyAmount,
  ): YearResult {
    const isRetirement = yearIndex >= t;
    const retirementYearIndex = isRetirement ? yearIndex - t : 0;

    let withdrawal = MoneyAmount.zero;
    if (isRetirement) {
      const w0 =
        scenario.monthlyExpenses.value *
        12 *
        Math.pow(1 + scenario.inflation.value, t);
      const wy = w0 * Math.pow(1 + scenario.inflation.value, retirementYearIndex);
      withdrawal = new MoneyAmount(wy);
    }

    const income = this.incomeForYear(scenario, yearIndex);
    const extraExpense = this.extraExpenseForYear(scenario, yearIndex);

    const portfolioPostWithdrawal = this.policy.applyStartOfYearCashflows({
      portfolioStart,
      withdrawal,
      income,
      extraExpense,
    });

    const portfolioEnd = this.policy.applyEndOfYearGrowth({
      portfolioPostWithdrawal,
      annualReturn: scenario.annualReturn.value,
    });

    return new YearResult({
      yearIndex,
      portfolioStart: portfolioStart.value,
      withdrawal: withdrawal.value,
      income: income.value,
      extraExpense: extraExpense.value,
      portfolioEnd: portfolioEnd.value,
      success: portfolioEnd.value >= 0,
    });
  }

  private incomeForYear(scenario: FireScenario, yearIndex: number): MoneyAmount {
    let total = MoneyAmount.zero;
    for (const event of scenario.events) {
      if (!event.enabled || event.kind !== EventKind.income) continue;
      const nominalAmount =
        this.getYearNominalAmount(scenario, event, yearIndex);
      if (nominalAmount.value > 0) {
        total = total.add(nominalAmount);
      }
    }
    return total;
  }

  private extraExpenseForYear(
    scenario: FireScenario,
    yearIndex: number,
  ): MoneyAmount {
    let total = MoneyAmount.zero;
    for (const event of scenario.events) {
      if (!event.enabled || event.kind !== EventKind.extraExpense) continue;
      const nominalAmount =
        this.getYearNominalAmount(scenario, event, yearIndex);
      if (nominalAmount.value > 0) {
        total = total.add(nominalAmount);
      }
    }
    return total;
  }

  /**
   * Compute the nominal annual contribution for a single event at the given
   * simulation year index. Returns MoneyAmount.zero when the event is not
   * active during this yearIndex.
   */
  private getYearNominalAmount(
    scenario: FireScenario,
    event: import('../entities/ScenarioEvent').ScenarioEvent,
    yearIndex: number,
  ): MoneyAmount {
    const startOffset = event.startAge.value - scenario.currentAge.value;

    // effectiveDuration: 0 = permanent (Infinity), N = N years from start
    const effectiveDuration =
      event.durationYears === 0 ? Infinity : event.durationYears;

    // Active if: yearIndex >= startOffset AND yearIndex < startOffset + effectiveDuration
    if (yearIndex < startOffset || yearIndex >= startOffset + effectiveDuration) {
      return MoneyAmount.zero;
    }

    // Years since event started
    const yearsSinceStart = yearIndex - startOffset;

    // Determine per-year growth rate
    const growth = this.getEventGrowthRate(scenario, event);

    // Annual base amount: monthly events × 12
    const annualBase =
      event.frequency === 'monthly'
        ? event.amount.value * 12
        : event.amount.value;

    // Nominal amount at this year
    const nominal = annualBase * Math.pow(1 + growth, yearsSinceStart);

    return new MoneyAmount(nominal);
  }

  /**
   * Determine the annual growth rate for an event.
   * - Pension events (pensionType set): use growthRate (defaults to 0% if undefined)
   * - Non-pension inflationAdjusted: use scenario.inflation
   * - Non-pension flat nominal: 0%
   */
  private getEventGrowthRate(
    _scenario: FireScenario,
    event: import('../entities/ScenarioEvent').ScenarioEvent,
  ): number {
    if (event.pensionType != null && event.pensionType !== 'none') {
      const defaultGrowth = getPensionDefaults(event.pensionType).growthRate;
      return event.growthRate ?? defaultGrowth;
    } else if (event.inflationAdjusted) {
      return _scenario.inflation.value;
    } else {
      return 0;
    }
  }
}